import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import {
  MessageSquare,
  X,
  Send,
  Sparkles,
  Bot,
  User,
  Lightbulb,
  Mail,
  Calendar,
  Target,
  TrendingUp,
  Clock,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUICK_ACTIONS = [
  { icon: Mail, label: 'Draft follow-up email', prompt: 'Help me draft a follow-up email for my leads' },
  { icon: Target, label: 'Which leads to prioritize?', prompt: 'Which of my leads should I prioritize today and why?' },
  { icon: TrendingUp, label: 'Analyze my pipeline', prompt: 'Analyze my current pipeline and suggest improvements' },
  { icon: Calendar, label: 'Schedule recommendations', prompt: 'What meetings or calls should I schedule this week?' },
];

const AIChat = () => {
  const { getAuthHeaders, user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Add welcome message
      setMessages([{
        id: 1,
        role: 'assistant',
        content: `Hi ${user?.name?.split(' ')[0] || 'there'}! I'm your AI sales assistant. I can help you:\n\n• Draft professional emails to leads\n• Prioritize your follow-ups\n• Analyze your pipeline performance\n• Suggest best times to contact leads\n\nHow can I help you today?`,
        timestamp: new Date()
      }]);
    }
  }, [isOpen, user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (message = inputValue) => {
    if (!message.trim()) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: message,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setSuggestions([]);

    try {
      const response = await axios.post(`${API_URL}/api/ai/chat`, {
        message: message,
        context: 'sales_assistant',
        history: messages.slice(-6).map(m => ({ role: m.role, content: m.content }))
      }, getAuthHeaders());

      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date(),
        suggestions: response.data.suggestions || []
      };

      setMessages(prev => [...prev, assistantMessage]);
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      // Simulate AI response for demo
      const simulatedResponse = await simulateAIResponse(message);
      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: simulatedResponse.response,
        timestamp: new Date(),
        suggestions: simulatedResponse.suggestions
      };
      setMessages(prev => [...prev, assistantMessage]);
      setSuggestions(simulatedResponse.suggestions || []);
    } finally {
      setIsLoading(false);
    }
  };

  const simulateAIResponse = async (message) => {
    await new Promise(resolve => setTimeout(resolve, 1000));

    const lowerMessage = message.toLowerCase();

    if (lowerMessage.includes('email') || lowerMessage.includes('draft')) {
      return {
        response: `Here's a professional follow-up email draft:\n\n---\n\n**Subject:** Following Up - Enable Healthcare Solutions\n\nDear [Contact Name],\n\nI hope this email finds you well. I wanted to follow up on our previous conversation about Enable Healthcare's solutions.\n\nGiven your practice's focus on [specialty], I believe our [EHR/RCM/CCM] platform could help you:\n\n• Reduce administrative burden by 40%\n• Improve patient outcomes with AI-powered insights\n• Increase revenue through optimized billing\n\nWould you have 15 minutes this week for a brief demo? I'm available [suggest 2-3 times].\n\nBest regards,\n${user?.name || 'Your Name'}\n\n---\n\nWould you like me to customize this for a specific lead?`,
        suggestions: ['Customize for Dr. Johnson', 'Make it more formal', 'Add pricing info']
      };
    }

    if (lowerMessage.includes('prioritize') || lowerMessage.includes('priority')) {
      return {
        response: `Based on your current pipeline, here are your top priority leads:\n\n**🔥 High Priority (Follow up today)**\n1. **Metro Health Clinic** - Demo scheduled, need confirmation\n   - Last contact: 3 days ago\n   - AI Score: 92/100\n\n2. **CardioCare Associates** - Showed strong interest in RCM\n   - Decision maker engaged\n   - AI Score: 88/100\n\n**⚡ Medium Priority (This week)**\n3. **Primary Care Partners** - Qualified but slow to respond\n   - Try calling instead of email\n   - AI Score: 75/100\n\n**💡 Tip:** Focus on Metro Health first - they're most likely to convert based on their engagement pattern.`,
        suggestions: ['Tell me more about Metro Health', 'Draft email for CardioCare', 'Show all leads']
      };
    }

    if (lowerMessage.includes('pipeline') || lowerMessage.includes('analyze')) {
      return {
        response: `Here's your pipeline analysis:\n\n**📊 Pipeline Health Score: 78/100**\n\n**Strengths:**\n✅ Good demo-to-close ratio (35%)\n✅ Average deal size increasing\n✅ Strong engagement in qualified stage\n\n**Areas to Improve:**\n⚠️ 5 leads stuck in "Contacted" for >14 days\n⚠️ Low conversion from New Lead → Contacted (45%)\n⚠️ 3 high-value leads need immediate follow-up\n\n**Recommendations:**\n1. Focus on moving stuck leads forward\n2. Implement a 48-hour follow-up rule for new leads\n3. Consider offering incentives for stalled deals\n\nWould you like specific actions for any of these?`,
        suggestions: ['Show stuck leads', 'Help with follow-up strategy', 'Forecast this quarter']
      };
    }

    if (lowerMessage.includes('schedule') || lowerMessage.includes('meeting') || lowerMessage.includes('call')) {
      return {
        response: `Based on your leads' activity patterns and best practices, here's your recommended schedule:\n\n**📅 Suggested Calls/Meetings This Week:**\n\n**Monday (Best for decision-makers)**\n• 10:00 AM - Dr. Johnson (Metro Health) - Demo follow-up\n• 2:00 PM - Dr. Chen (CardioCare) - Discovery call\n\n**Tuesday**\n• 11:00 AM - Dr. Rodriguez (Primary Care) - Re-engagement call\n\n**Wednesday (Peak engagement day)**\n• 9:00 AM - Dr. Wilson (OrthoPlus) - Demo\n• 3:00 PM - Dr. Park (Pediatric Care) - Proposal review\n\n**💡 Pro Tips:**\n• Healthcare professionals are most responsive 9-11 AM\n• Avoid calling during lunch (12-1 PM) or after 4 PM\n• Tuesday-Thursday have highest connection rates`,
        suggestions: ['Add to calendar', 'Prepare for Metro Health demo', 'Send meeting invites']
      };
    }

    // Default response
    return {
      response: `I can help you with that! Here are some things I can assist with:\n\n• **Email drafting** - Professional follow-up and outreach emails\n• **Lead prioritization** - AI-powered scoring and recommendations\n• **Pipeline analysis** - Insights and improvement suggestions\n• **Scheduling** - Best times to contact your leads\n• **Data enrichment** - Finding missing contact info\n\nWhat would you like to focus on?`,
      suggestions: ['Draft an email', 'Prioritize my leads', 'Analyze pipeline']
    };
  };

  const handleQuickAction = (action) => {
    handleSendMessage(action.prompt);
  };

  const handleSuggestionClick = (suggestion) => {
    handleSendMessage(suggestion);
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <motion.button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-6 w-14 h-14 bg-gradient-to-br from-[#1B365D] to-[#0071E3] rounded-full shadow-lg flex items-center justify-center text-white z-50 hover:shadow-xl transition-shadow"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        data-testid="ai-chat-toggle"
      >
        <MessageSquare className="w-6 h-6" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center">
          <Sparkles className="w-3 h-3" />
        </span>
      </motion.button>

      {/* Chat Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-20 right-6 w-96 h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden border border-slate-200"
            data-testid="ai-chat-panel"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#1B365D] to-[#0071E3] p-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <Bot className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold">AI Sales Assistant</h3>
                    <p className="text-xs text-white/70">Powered by Enable AI</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {messages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {message.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1B365D] to-[#0071E3] flex items-center justify-center flex-shrink-0">
                        <Bot className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div className={`max-w-[80%] rounded-2xl p-3 ${
                      message.role === 'user' 
                        ? 'bg-[#1B365D] text-white rounded-br-md' 
                        : 'bg-slate-100 text-slate-900 rounded-bl-md'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                    {message.role === 'user' && (
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-slate-600" />
                      </div>
                    )}
                  </motion.div>
                ))}

                {isLoading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex gap-3"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1B365D] to-[#0071E3] flex items-center justify-center">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-slate-100 rounded-2xl rounded-bl-md p-3">
                      <div className="flex items-center gap-2 text-slate-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Thinking...</span>
                      </div>
                    </div>
                  </motion.div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Suggestions */}
            {suggestions.length > 0 && (
              <div className="px-4 py-2 border-t border-slate-100">
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((suggestion, idx) => (
                    <Button
                      key={idx}
                      variant="outline"
                      size="sm"
                      className="text-xs rounded-full"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      {suggestion}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Actions (only show when no messages or few messages) */}
            {messages.length <= 1 && (
              <div className="px-4 py-2 border-t border-slate-100">
                <p className="text-xs text-slate-500 mb-2">Quick actions:</p>
                <div className="grid grid-cols-2 gap-2">
                  {QUICK_ACTIONS.map((action, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleQuickAction(action)}
                      className="flex items-center gap-2 p-2 text-left text-xs bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                      <action.icon className="w-4 h-4 text-[#0071E3]" />
                      <span className="text-slate-700">{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="p-4 border-t border-slate-100">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSendMessage();
                }}
                className="flex items-center gap-2"
              >
                <Input
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Ask me anything..."
                  className="flex-1 rounded-full"
                  disabled={isLoading}
                  data-testid="ai-chat-input"
                />
                <Button
                  type="submit"
                  size="icon"
                  className="rounded-full bg-[#1B365D] hover:bg-[#1B365D]/90"
                  disabled={isLoading || !inputValue.trim()}
                  data-testid="ai-chat-send"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIChat;
